<div class="comprehensive-liveness-verification-report">    
<!-- Page Control for export of data -->
    <PageControl
      :title="'Comprehensive Liveness Verification Report'"
      :elements_query="sheet_elements"
    >
      <button
        @click="show_export_modal"
        class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
      >
        <i class="bi bi-download"></i>
        Export Data
      </button>

      <!-- Export Modal -->
      <Teleport to="body">
        <div
          class="modal fade"
          :id="'export-modal-' + report_reference"
          tabindex="-1"
          aria-labelledby="exportModalLabel"
          aria-hidden="true"
        >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content rounded-4 shadow">
            <div class="modal-header">
              <h6 class="modal-title fw-semibold text-muted">
                Export Comprehensive Liveness Verification Report Data
              </h6>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label fw-bold">Select Data to Export:</label>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.personal_information"
                    id="personalInformation"
                  />
                  <label class="form-check-label" for="personalInformation">
                    Personal Information (OCR Document Details)
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.facial_comparison_liveness"
                    id="facialComparisonLiveness"
                  />
                  <label class="form-check-label" for="facialComparisonLiveness">
                    Facial Comparison & Liveness Analysis
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.document_authentication"
                    id="documentAuthentication"
                  />
                  <label class="form-check-label" for="documentAuthentication">
                    Document Authentication Analysis
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.raw_json"
                    id="rawJson"
                  />
                  <label class="form-check-label" for="rawJson">
                    Raw JSON Response Data
                  </label>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-bold">Export Format:</label>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="csv"
                    id="csvFormat"
                  />
                  <label class="form-check-label" for="csvFormat">
                    CSV (Separate files for each table)
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="excel"
                    id="excelFormat"
                  />
                  <label class="form-check-label" for="excelFormat">
                    Excel (Multiple sheets in one file)
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="json"
                    id="jsonFormat"
                  />
                  <label class="form-check-label" for="jsonFormat">
                    JSON (JavaScript Object Notation)
                  </label>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
                @click="close_modal"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-bg-green text-xs rounded-small shadow-sm"
                @click="export_data"
                :disabled="!has_selected_tables"
              >
                <i class="bi bi-download me-1"></i>
                Export Data
              </button>
            </div>
          </div>        </div>
      </div>
      </Teleport>
    </PageControl>
    
    <PageComponent>
      <SectionSheet>
        <div
          class="container-fluid py-3 header d-flex align-items-center justify-content-between border-bottom mb-3"
        >
          <div class="d-flex">
            <img
              class="logo"
              width="295"
              height="125"
              src="@/assets/images/new_black_logo2.svg"
              alt="Logo"
            />
          </div>
          <div class="d-flex text-end">
            <div>
              <div class="h4 fw-bold mb-1">Comprehensive Liveness Verification</div>
              <div class="text-muted text-xs">
                28 Fricker Rd,<br />
                Illovo, Johannesburg,<br />
                2196 South Africa<br />
                Website:
                <a
                  href="https://www.goodpeopledata.com/"
                  target="_blank"
                  class="text-decoration-none text-muted"
                  >www.goodpeopledata.com</a
                >
              </div>
            </div>
          </div>
        </div>

        <div class="card shadow-sm border bg-white mb-2">
          <div class="card-header">
            <h6 class="mb-0">Comprehensive Liveness Verification Summary</h6>
          </div>
          <div class="card-body text-xs">
            <p>
              This Comprehensive Liveness verification report presents the results for
              <span class="fw-bold text-dark">{{ full_name }}</span>, 
              <span class="fw-bold text-dark">{{ age }} years of age</span>, 
              <span class="fw-bold text-dark">{{ formatted_sex }}</span>, and
              {{ citizenship_status.toLowerCase() }} of {{ nationality }}. The
              comprehensive verification process comprised three primary components:
              <br>
              <br>
              (1) Liveness detection analysis of the provided comparison image,
              (2) Optical character recognition analysis of a
              <span class="fw-bold text-dark">{{ document_description }}</span>
              issued by
              <span class="fw-bold text-dark">{{ issuing_country }}</span> in {{
              document_year
              }}, and 
              (3) Facial comparison analysis between the comparison image and document
              portrait. The verification assessment results are summarized below:
            </p>
            <!-- Clean Status Cards for A4 Print -->
            <div class="row g-2">
              <div class="col-md-4">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-person-check"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Liveness Status</div>
                    <div class="status-value-simple bg-white" :class="liveness_status_class">
                      {{ liveness_status_text }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-person-badge"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Facial Match</div>
                    <div class="status-value-simple bg-white" :class="facial_match_class">
                      {{ facial_confidence }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-file-earmark-check"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Document Auth</div>
                    <div class="status-value-simple bg-white" :class="document_auth_class">
                      {{ document_auth_text }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card shadow-sm border bg-white mb-2">
          <div class="card-header">
            <h6 class="mb-0">Personal Information</h6>
          </div>          
          <div class="card-body">
            <div class="row g-3">
              <!-- Full Name -->
              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-person-circle"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Full Name</div>
                    <div class="status-value-simple bg-white">
                      {{ full_name }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Given Names -->
              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-person-badge"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Given Names</div>
                    <div class="status-value-simple bg-white">
                      {{ given_names }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Surname -->
              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-person-vcard"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Surname</div>
                    <div class="status-value-simple bg-white">
                      {{ surname }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Date of Birth -->
              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-calendar-event"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Date of Birth</div>
                    <div class="status-value-simple bg-white">
                      {{ formatted_date_of_birth }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Age -->
              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-hourglass-split"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Age</div>
                    <div class="status-value-simple bg-white">
                      {{ age }} years old
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Sex -->
              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-gender-ambiguous"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Sex</div>
                    <div class="status-value-simple bg-white">
                      {{ formatted_sex }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Personal Number -->
              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-hash"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">ID Number</div>
                    <div class="status-value-simple bg-white">
                      {{ personal_number }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Citizenship Status -->
              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-shield-check"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Citizenship Status</div>
                    <div class="status-value-simple bg-white">
                      {{ citizenship_status }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Nationality -->
              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-flag"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Nationality</div>
                    <div class="status-value-simple bg-white">
                      {{ nationality }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Place of Birth -->
              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-geo-alt"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Place of Birth</div>
                    <div class="status-value-simple bg-white">
                      {{ place_of_birth }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-geo-alt"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Marital Status</div>
                    <div class="status-value-simple bg-white">
                      {{ marital_status }}
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="status-card-simple">
                  <div class="status-icon-simple">
                    <i class="bi bi-geo-alt"></i>
                  </div>
                  <div class="status-content-simple">
                    <div class="status-label-simple">Smart Card</div>
                    <div class="status-value-simple bg-white">
                      {{ smart_card_status }}
                    </div>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
        </div>

        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>      <!-- Facial Comparison Analysis - Enhanced Section -->
      <SectionSheet>
      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">Verification Images</h6>
        </div>
        <div class="card-body">
          <!-- Three Face Images Side by Side -->
          <h6 class="mb-3 border-bottom pb-2">
            <i class="bi bi-images me-2"></i>Facial Images
          </h6>
          
          <div class="row g-3 mb-4">
            <!-- Home Affairs Image -->
            <div class="col-md-4">
              <div class="image-section">
                <h6 class="image-title">Home Affairs</h6>
                <div class="image-container11">
                  <img
                    v-if="selfie_image"
                    :src="selfie_image_src"
                    alt="Home Affairs"
                    class="verification-image"
                  />
                  <div v-else class="no-image">
                    <i class="bi bi-person-circle"></i>
                    <p>No Home Affairs image available</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="image-section">
                <h6 class="image-title">Captured image</h6>
                <div class="image-container1">
                  <img
                  v-if="comparison_image"
                  :src="comparison_image_src"
                  alt="Comparison"
                  class="verification-image"
                />
                  <div v-else class="no-image">
                    <i class="bi bi-person-circle"></i>
                    <p>No Captured image available</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="image-section">
                <h6 class="image-title">OCR Image</h6>
                <div class="image-container1">
                  <img
                    v-if="portrait_image"
                    :src="portrait_image"
                    alt="OCR Portrait"
                    class="verification-image"
                  />
                  <div v-else class="no-image">
                    <i class="bi bi-person-circle"></i>
                    <p>No OCR image available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Document Front Side Image -->
          <h6 class="mb-3 border-bottom pb-2">
            <i class="bi bi-file-earmark-image me-2"></i>Document Image
          </h6>
          
          <div class="row">
            <div class="col-12">
              <div class="image-section">
                <h6 class="image-title">Document Front Side</h6>
                <div class="image-container1" style="max-width: 600px; margin: 0 auto;">
                  <img
                    v-if="document_front_image"
                    :src="document_front_image"
                    alt="Document Front Side"
                    class="verification-image"
                    style="width: 100%; height: auto;"
                  />
                  <div v-else class="no-image">
                    <i class="bi bi-file-earmark-x"></i>
                    <p>No document image available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>
        <!-- Liveness Detection Analysis & Methods - Combined Section -->
            <SectionSheet>
        <div class="card shadow-sm border bg-white mb-3">
          <div class="card-header bg-light">
            <h6 class="mb-0">Facial Comparison Analysis</h6>
          </div>
          <div class="card-body">
            <!-- Comparison 1: Liveness Image vs OCR Portrait -->
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-arrow-left-right me-2"></i>Captured Image vs OCR Portrait
            </h6>
            <div class="table-responsive mb-4">
              <table class="table table-bordered align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Metric</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Similarity Score</td>
                    <td>{{ facial_similarity_rounded }}</td>
                    <td>Numerical similarity between captured image and OCR portrait (0-1 scale).</td>
                  </tr>
                  <tr>
                    <td>Similarity Percentage</td>
                    <td>{{ facial_similarity_percentage }}%</td>
                    <td>Similarity expressed as a percentage.</td>
                  </tr>
                  <tr>
                    <td>Confidence Level</td>
                    <td :class="facial_match_class">{{ facial_confidence }}</td>
                    <td>Confidence assessment of the facial match.</td>
                  </tr>
                  <tr>
                    <td>Message</td>
                    <td colspan="2">{{ facial_message }}</td>
                  </tr>
                  <tr>
                    <td>Recommendation</td>
                    <td :class="facial_recommendation_class">{{ facial_recommendation }}</td>
                    <td>System recommendation based on similarity analysis.</td>
                  </tr>
                  <tr>
                    <td>Selfie Quality</td>
                    <td>{{ selfie_quality_level }}</td>
                    <td>Quality assessment of the captured selfie image (Blur: {{ selfie_blur_score }}).</td>
                  </tr>
                  <tr>
                    <td>OCR Portrait Quality</td>
                    <td>{{ comparison_quality_level }}</td>
                    <td>Quality assessment of the OCR portrait image (Blur: {{ comparison_blur_score }}).</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Comparison 2: Home Affairs Image vs OCR Portrait -->
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-arrow-left-right me-2"></i>Home Affairs Image vs OCR Portrait
            </h6>
            <div class="table-responsive mb-4">
              <table class="table table-bordered align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Metric</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Similarity Score</td>
                    <td>{{ dha_ocr_similarity_rounded }}</td>
                    <td>Numerical similarity between Home Affairs image and OCR portrait (0-1 scale).</td>
                  </tr>
                  <tr>
                    <td>Similarity Percentage</td>
                    <td>{{ dha_ocr_similarity_percentage }}%</td>
                    <td>Similarity expressed as a percentage.</td>
                  </tr>
                  <tr>
                    <td>Confidence Level</td>
                    <td :class="facial_match_class">{{ dha_ocr_confidence }}</td>
                    <td>Confidence assessment of the facial match.</td>
                  </tr>
                  <tr>
                    <td>Message</td>
                    <td colspan="2">{{ dha_ocr_message }}</td>
                  </tr>
                  <tr>
                    <td>Recommendation</td>
                    <td :class="dha_ocr_recommendation_class">{{ dha_ocr_recommendation }}</td>
                    <td>System recommendation based on similarity analysis.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Home Affairs Internal Face Result -->
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-bank me-2"></i>Home Affairs Internal Face Verification
            </h6>
            <div class="table-responsive">
              <table class="table table-bordered align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Metric</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Face Match Result</td>
                    <td>
                      <span class="badge" :class="face_is_identical ? 'bg-success' : 'bg-danger'">
                        {{ face_is_identical ? 'Identical' : 'Different' }}
                      </span>
                    </td>
                    <td>Home Affairs internal face comparison result.</td>
                  </tr>
                  <tr>
                    <td>DHA Confidence</td>
                    <td>{{ face_confidence }}%</td>
                    <td>Confidence level from Home Affairs face comparison system.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>
      <SectionSheet>
        <!-- Liveness Detection Analysis Table -->
        <div class="card shadow-sm border bg-white mb-3">
            <div class="card-header bg-light d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Liveness Analysis</h6>
            </div>
            <div class="card-body">
                <!-- Liveness Detection Analysis Table -->
                <h6 class="mb-3 border-bottom pb-2">
                  <i class="bi bi-person-check me-2"></i>Liveness Detection Analysis
                </h6>
                <div class="table-responsive mb-4">
                  <table class="table table-bordered align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Metric</th>
                        <th>Value</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Liveness Score</td>
                        <td>{{ adjusted_liveness_score_rounded }}</td>
                        <td>The score indicating the likelihood that the presented face is from a live person rather than a spoof or photo.</td>
                      </tr>
                      <tr>
                        <td>Quality Score</td>
                        <td>{{ quality_overall_score_rounded }}</td>
                        <td>Overall quality of the image used for liveness detection, considering clarity, lighting, and focus.</td>
                      </tr>
                      <tr>
                        <td>Final Decision</td>
                        <td :class="liveness_status_class">{{ final_decision }}</td>
                        <td>The system's decision on whether the liveness check passed or failed.</td>
                      </tr>
                      <tr>
                        <td>Confidence</td>
                        <td>{{ liveness_confidence_rounded }}</td>
                        <td>Confidence level in the liveness assessment, on a scale from 0 to 1.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
            </div>
        </div>

        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>
      <SectionSheet >
        <div class="card shadow-sm border bg-white mb-3">
            <div class="card-header bg-light d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Optical Character Recognition Analysis</h6>
            </div>
            <div class="card-body">
                <!-- Authentication Checks Table -->
                <h6 class="mb-3 border-bottom pb-2">
                  <i class="bi bi-check-square me-2"></i>Authentication Checks
                </h6>
                <div class="table-responsive mb-4">
                  <table class="table table-bordered align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Check</th>
                        <th>Status</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><i class="bi bi-file-check me-2 text-secondary"></i>Document Type</td>
                        <td>
                          <span class="badge" :class="auth_check_class('docType') === 'text-success' ? 'bg-success' : 
                                auth_check_class('docType') === 'text-warning' ? 'bg-warning text-dark' : 
                                auth_check_class('docType') === 'text-danger' ? 'bg-danger' : 'bg-secondary'">
                            {{ auth_check_text('docType') }}
                          </span>
                        </td>
                        <td>Verifies that the document type matches expected formats and standards.</td>
                      </tr>
                      <tr>
                        <td><i class="bi bi-calendar-x me-2 text-secondary"></i>Expiry Check</td>
                        <td>
                          <span class="badge" :class="auth_check_class('expiry') === 'text-success' ? 'bg-success' : 
                                auth_check_class('expiry') === 'text-warning' ? 'bg-warning text-dark' : 
                                auth_check_class('expiry') === 'text-danger' ? 'bg-danger' : 'bg-secondary'">
                            {{ auth_check_text('expiry') }}
                          </span>
                        </td>
                        <td>Checks if the document is still valid or has expired.</td>
                      </tr>
                      <tr>
                        <td><i class="bi bi-image me-2 text-secondary"></i>Image Quality</td>
                        <td>
                          <span class="badge" :class="auth_check_class('imageQA') === 'text-success' ? 'bg-success' : 
                                auth_check_class('imageQA') === 'text-warning' ? 'bg-warning text-dark' : 
                                auth_check_class('imageQA') === 'text-danger' ? 'bg-danger' : 'bg-secondary'">
                            {{ auth_check_text('imageQA') }}
                          </span>
                        </td>
                        <td>Assesses the clarity and suitability of the document image for verification.</td>
                      </tr>
                      <tr>
                        <td><i class="bi bi-upc-scan me-2 text-secondary"></i>MRZ Check</td>
                        <td>
                          <span class="badge" :class="auth_check_class('mrz') === 'text-success' ? 'bg-success' : 
                                auth_check_class('mrz') === 'text-warning' ? 'bg-warning text-dark' : 
                                auth_check_class('mrz') === 'text-danger' ? 'bg-danger' : 'bg-secondary'">
                            {{ auth_check_text('mrz') }}
                          </span>
                        </td>
                        <td>Validates the Machine Readable Zone (MRZ) for accuracy and authenticity.</td>
                      </tr>
                      <tr>
                        <td><i class="bi bi-lock me-2 text-secondary"></i>Security Features</td>
                        <td>
                          <span class="badge" :class="auth_check_class('security') === 'text-success' ? 'bg-success' : 
                                auth_check_class('security') === 'text-warning' ? 'bg-warning text-dark' : 
                                auth_check_class('security') === 'text-danger' ? 'bg-danger' : 'bg-secondary'">
                            {{ auth_check_text('security') }}
                          </span>
                        </td>
                        <td>Checks for the presence of security features such as holograms or watermarks.</td>
                      </tr>
                      <tr>
                        <td><i class="bi bi-fonts me-2 text-secondary"></i>Text Analysis</td>
                        <td>
                          <span class="badge" :class="auth_check_class('text') === 'text-success' ? 'bg-success' : 
                                auth_check_class('text') === 'text-warning' ? 'bg-warning text-dark' : 
                                auth_check_class('text') === 'text-danger' ? 'bg-danger' : 'bg-secondary'">
                            {{ auth_check_text('text') }}
                          </span>
                        </td>
                        <td>Analyzes the text on the document for consistency and authenticity.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
            </div>
        </div>

        
        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>
      
      <!-- Disclaimer and Information Section -->
      <SectionSheet>
        <div class="card shadow-sm border bg-white mb-2">
          <div class="card-header bg-light">
            <h6 class="mb-0">Disclaimer and Information</h6>
          </div>
          <div class="card-body">
            <div class="disclaimer-content">
              <h6 class="mb-3 border-bottom pb-2">
                <i class="bi bi-info-circle me-2"></i>General Information
              </h6>
              <p class="text-xs mb-4">
                This Comprehensive Liveness Verification Report provides a technical assessment of identity documents and biometric data. 
                The verification process includes document authentication, facial comparison, and liveness detection to help prevent fraud.
              </p>
              
              <h6 class="mb-3 border-bottom pb-2">
                <i class="bi bi-shield-lock me-2"></i>Data Privacy
              </h6>
              <p class="text-xs mb-4">
                All biometric data and document information are processed in accordance with applicable data protection laws.
                This report should only be shared with authorized personnel who require access to this information for legitimate business purposes.
              </p>
              
              <h6 class="mb-3 border-bottom pb-2">
                <i class="bi bi-gear me-2"></i>Technical Notes
              </h6>
              <p class="text-xs mb-4">
                Similarity scores and confidence levels are calculated using proprietary algorithms that measure the likelihood
                of document authenticity and facial matching. Environmental factors such as lighting, image quality, and camera
                specifications may impact verification results.
              </p>
              
              <div class="text-center text-muted mt-3">
                <p class="mb-0 text-xs">
                  © {{ new Date().getFullYear() }} The Good People Data Company. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>
    </PageComponent>
  </div>